import { useMemo } from 'react';
import { useClients } from '../../lib/filters.hooks';
import type { ScopeOption, ScopeState } from './types';

interface ScopeOptions {
  brandOptions: ScopeOption[];
  platformOptions: ScopeOption[];
  accountOptions: ScopeOption[];
}

const byLabel = (a: ScopeOption, b: ScopeOption): number => a.label.localeCompare(b.label);

export function useScopeOptions(scope: ScopeState): ScopeOptions {
  const { data: clients } = useClients();

  const allAccounts = useMemo(
    () => (clients ?? []).flatMap((client) => client.accounts),
    [clients],
  );

  const brandOptions = useMemo(
    () =>
      [...new Map(allAccounts.map((account) => [account.productId, account.productName])).entries()]
        .map(([id, label]) => ({ id: String(id), label }))
        .sort(byLabel),
    [allAccounts],
  );

  const accounts = useMemo(
    () =>
      scope.brand.length
        ? allAccounts.filter((account) => scope.brand.includes(String(account.productId)))
        : allAccounts,
    [allAccounts, scope.brand],
  );

  const platformOptions = useMemo(
    () =>
      [
        ...new Map(
          accounts
            .filter((account) => account.network !== null)
            .map((account) => [account.network as string, account.networkLabel]),
        ).entries(),
      ]
        .map(([network, label]) => ({ id: network, label }))
        .sort(byLabel),
    [accounts],
  );

  const accountOptions = useMemo(() => {
    const inScope = accounts.filter(
      (account) =>
        scope.platform.length === 0 ||
        (account.network !== null && scope.platform.includes(account.network)),
    );
    const nameCounts = new Map<string, number>();
    for (const account of inScope) {
      nameCounts.set(account.name, (nameCounts.get(account.name) ?? 0) + 1);
    }
    return inScope
      .map((account) => ({
        id: String(account.id),
        label:
          (nameCounts.get(account.name) ?? 0) > 1
            ? `${account.name} - ${account.networkLabel}`
            : account.name,
      }))
      .sort(byLabel);
  }, [accounts, scope.platform]);

  return { brandOptions, platformOptions, accountOptions };
}
